import React from 'react';
import { Paper, Box, Typography } from '@mui/material';
import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined';
import { useTranslation } from 'react-i18next';
import { IC, IFontSizes, IFontWeights, IRadius, IHeroNumericSx, INumericSx } from '../insightsTheme';

/**
 * InactionWidget — Myma-native dark card expressing cost of inaction.
 *
 * Props:
 *   alertCount:  number of open alerts
 *   agingDays:   average days alerts stay open (nullable)
 *   monthlyLoss: € per month (number)
 *   dailyLoss:   optional; else derived as monthlyLoss/30
 *   width:       card width in px (default 220 — wider than before for Roboto)
 */
const fmt = (v) => {
  const n = Math.abs(Number(v) || 0);
  return n.toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: true });
};

const InactionWidget = ({
  alertCount = 0,
  agingDays = null,
  monthlyLoss = 0,
  dailyLoss,
  width = 220,
  onAlertsClick,
  onLossClick,
}) => {
  const { t } = useTranslation();
  const daily = dailyLoss != null ? dailyLoss : Math.round((monthlyLoss || 0) / 30);

  const clickableSx = (fn) => (fn
    ? {
        cursor: 'pointer',
        transition: 'background 0.15s ease',
        '&:hover': { background: 'rgba(234, 87, 46, 0.10)' },
      }
    : null);

  return (
    <Paper
      variant="outlined"
      sx={{
        width,
        flexShrink: 0,
        borderRadius: `${IRadius.card}px`,
        borderColor: IC.bdr,
        borderLeft: `3px solid ${IC.ai}`,
        background: IC.surface,
        px: 2,
        py: 1.75,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <ReportGmailerrorredOutlinedIcon sx={{ fontSize: 14, color: IC.ai }} />
        <Typography
          sx={{
            fontSize: `${IFontSizes.sm}px`,
            fontWeight: IFontWeights.strong,
            color: IC.tx,
          }}
        >
          {t('insights_v2_inaction_title')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          role={onAlertsClick ? 'button' : undefined}
          tabIndex={onAlertsClick ? 0 : undefined}
          onClick={onAlertsClick || undefined}
          onKeyDown={(e) => {
            if (onAlertsClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onAlertsClick();
            }
          }}
          aria-label={onAlertsClick ? t('insights_v2_a11y_open_inaction_alerts') : undefined}
          sx={{
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            mx: -0.75,
            my: -0.25,
            ...clickableSx(onAlertsClick),
          }}
        >
          <Typography
            sx={{
              fontSize: `${IFontSizes.xs}px`,
              color: IC.txM,
            }}
          >
            {t('insights_v2_inaction_alerts')}
          </Typography>
          <Typography
            sx={{ fontSize: `${IFontSizes.lg}px`, fontWeight: IFontWeights.strong, color: IC.tx, ...IHeroNumericSx }}
          >
            {alertCount}
          </Typography>
        </Box>
        {agingDays != null && (
          <Box>
            <Typography
              sx={{
                fontSize: `${IFontSizes.xs}px`,
                color: IC.txM,
              }}
            >
              {t('insights_v2_inaction_aging')}
            </Typography>
            <Typography
              sx={{ fontSize: `${IFontSizes.lg}px`, fontWeight: IFontWeights.strong, color: IC.tx, ...IHeroNumericSx }}
            >
              {agingDays}
              {t('insights_v2_inaction_days_short')}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        role={onLossClick ? 'button' : undefined}
        tabIndex={onLossClick ? 0 : undefined}
        onClick={onLossClick || undefined}
        onKeyDown={(e) => {
          if (onLossClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onLossClick();
          }
        }}
        aria-label={onLossClick ? t('insights_v2_a11y_open_inaction_loss') : undefined}
        sx={{
          background: IC.aiSoft,
          border: `1px solid ${IC.aiSoft}`,
          borderRadius: 1,
          px: 1.25,
          py: 1,
          cursor: onLossClick ? 'pointer' : 'default',
          transition: 'background 0.15s ease',
          '&:hover': onLossClick ? { background: 'rgba(234, 87, 46, 0.18)' } : undefined,
        }}
      >
        <Typography sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.tx2 }}>
          {t('insights_v2_inaction_monthly_loss')}
        </Typography>
        <Typography
          sx={{
            fontSize: `${IFontSizes.lg}px`,
            fontWeight: IFontWeights.strong,
            color: IC.ai,
            ...IHeroNumericSx,
          }}
        >
          -{fmt(monthlyLoss)}€
        </Typography>
      </Box>

      <Typography
        sx={{ fontSize: `${IFontSizes.xs}px`, color: IC.txM, lineHeight: 1.4 }}
      >
        {t('insights_v2_inaction_daily')}{' '}
        <Box component="strong" sx={{ color: IC.ai, fontWeight: IFontWeights.strong, ...INumericSx }}>
          ~{fmt(daily)}€
        </Box>
      </Typography>
    </Paper>
  );
};

export default InactionWidget;
